import { FC, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DataGrid } from "../../../../components/datagrid";
import { pruneFilterModel, useGridView } from "../../../../atoms/gridViewAtoms";
import { useCounterpartyColumns } from "./hooks/useCounterpartyColumns";
import { useNavigate } from "react-router-dom";
import { useCounterpartyOverview } from "../../dataHooks/counterpartyQueries";
import { useRoles } from "../../../../auth/useRoles";
import {
  hasAnyAppRole,
  isGlobalLifecycleRole
} from "../../../../auth/permissions";

type Props = {};

export const CounterpartiesGrid: FC<Props> = () => {
  const { data, isLoading, isFetching } = useCounterpartyOverview();

  const rows = data || [];
  const navigate = useNavigate();
  const roles = useRoles();
  const columns = useCounterpartyColumns(rows, isGlobalLifecycleRole(roles));
  const [view, setView] = useGridView("counterparties");

  // The MA Handler column only exists for global lifecycle roles, so a view saved by an admin
  // can carry a filter on a field this user has no column for.
  const filterModel = useMemo(
    () =>
      pruneFilterModel(view.filterModel, new Set(columns.map((c) => c.field))),
    [view.filterModel, columns]
  );

  const showGrantModeEmptyState =
    !isLoading && !isFetching && rows.length === 0 && !hasAnyAppRole(roles);

  if (showGrantModeEmptyState) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          textAlign: "center"
        }}
      >
        <Typography variant="h6">
          You do not have access to any counterparties yet.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={() => navigate("/tradingcounterparties/request-new")}
          >
            Request a new counterparty
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/tradingcounterparties/my-requests")}
          >
            My requests
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <DataGrid
      loading={isLoading || isFetching}
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      getRowHref={(row) =>
        `/tradingcounterparties/counterparty/${row.id}`
      }
      searchPlaceholder="Search counterparties"
      exportFileName="Counterparties"
      filterModel={filterModel}
      onFilterModelChange={(next) =>
        setView((prev) => ({ ...prev, filterModel: next }))
      }
      sortModel={view.sortModel}
      onSortModelChange={(next) =>
        setView((prev) => ({ ...prev, sortModel: next }))
      }
      sx={{
        "&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
          outline: "none"
        }
      }}
    />
  );
};
